import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import {
  FaSave,
  FaTimes,
  FaEye,
  FaEdit,
  FaPlus,
  FaSpinner,
  FaInfoCircle,
  FaCheckCircle,
  FaExclamationTriangle,
  FaVideo,
  FaTrash,
  FaUpload,
  FaPlay
} from 'react-icons/fa';
import { MdLibraryBooks, MdPreview } from 'react-icons/md';
import RichTextEditor from '../../components/RichTextEditor';
import { useNavigate } from 'react-router-dom';
import './ManualEditor.css';

const api = axios.create({
    baseURL: 'https://hms.backend.hiragroupcloud.com/api',
    headers: { 'Content-Type': 'application/json' }
});

const ManualEditor = () => {
    const navigate = useNavigate();
    const [title, setTitle] = useState('');
    const [content, setContent] = useState('');
    const [videos, setVideos] = useState([]);
    const [loading, setLoading] = useState(false);
    const [fetching, setFetching] = useState(true);
    const [existingManual, setExistingManual] = useState(null);
    const [isEditing, setIsEditing] = useState(false);
    const [uploadingVideo, setUploadingVideo] = useState(false);
    const [videoTitle, setVideoTitle] = useState('');
    const [videoDescription, setVideoDescription] = useState('');
    const [showVideoUpload, setShowVideoUpload] = useState(false);

    useEffect(() => {
        fetchManual();
    }, []);

    const fetchManual = async () => {
        try {
            console.log('Fetching existing manual...');
            const response = await api.get('/manuals');

            if (response.data.success && response.data.data) {
                const manual = response.data.data;
                setTitle(manual.title);
                setContent(manual.content);
                setVideos(manual.videos || []);
                setExistingManual(manual);
                setIsEditing(true);
                console.log('Manual loaded:', manual.title);
                console.log('Videos loaded:', manual.videos?.length || 0);
            }
        } catch (error) {
            console.error('Error fetching manual:', error);
            if (error.response?.status === 404) {
                console.log('No existing manual found, ready to create new');
            }
        } finally {
            setFetching(false);
        }
    };

// In ManualEditor.js, update the handleVideoUpload function:

const handleVideoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!videoTitle.trim()) {
        showWarningAlert('Please enter a video title');
        return;
    }

    if (!file.type.startsWith('video/')) {
        showErrorAlert('Please upload a valid video file');
        return;
    }

    if (file.size > 100 * 1024 * 1024) {
        showErrorAlert('Video size must be less than 100MB');
        return;
    }

    const formData = new FormData();
    formData.append('video', file);
    formData.append('title', videoTitle);
    formData.append('description', videoDescription);

    setUploadingVideo(true);

    try {
        const response = await api.post('/manuals/upload-video', formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
        });

        if (response.data.success) {
            // Refresh the manual to get updated videos list
            await fetchManual(); // This will reload the entire manual with updated videos
            setVideoTitle('');
            setVideoDescription('');
            setShowVideoUpload(false);
            showSuccessAlert('Video uploaded successfully!');
        }
    } catch (error) {
        console.error('Error uploading video:', error);
        showErrorAlert(error.response?.data?.message || 'Error uploading video');
    } finally {
        setUploadingVideo(false);
    }
};

// In your ManualEditor.js, update the video deletion handler:

const handleDeleteVideo = async (videoId) => {
    console.log('Attempting to delete video with ID:', videoId);

    const result = await Swal.fire({
        title: 'Delete Video',
        text: 'Are you sure you want to delete this video?',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#dc3545',
        cancelButtonColor: '#6c757d',
        confirmButtonText: 'Yes, delete',
        cancelButtonText: 'Cancel'
    });

    if (result.isConfirmed) {
        try {
            const response = await api.delete(`/manuals/video/${videoId}`);
            if (response.data.success) {
                // Update videos list after successful deletion
                setVideos(response.data.data.videos || []);
                showSuccessAlert('Video deleted successfully!');

                // Refresh manual data
                await fetchManual();
            } else {
                showErrorAlert('Failed to delete video');
            }
        } catch (error) {
            console.error('Error deleting video:', error);
            showErrorAlert(error.response?.data?.message || 'Error deleting video');
        }
    }
};
    const showSuccessAlert = (message, isUpdate = false) => {
        Swal.fire({
            title: isUpdate ? 'Manual Updated' : 'Success',
            text: message,
            icon: 'success',
            confirmButtonColor: '#004aad',
            confirmButtonText: 'OK',
            timer: 3000,
            timerProgressBar: true,
            backdrop: true,
            allowOutsideClick: false,
            customClass: {
                popup: 'professional-swal',
                title: 'swal-title',
                confirmButton: 'swal-confirm-btn'
            }
        });
    };

    const showErrorAlert = (message) => {
        Swal.fire({
            title: 'Operation Failed',
            text: message,
            icon: 'error',
            confirmButtonColor: '#dc3545',
            confirmButtonText: 'Try Again',
            customClass: {
                popup: 'professional-swal',
                confirmButton: 'swal-error-btn'
            }
        });
    };

    const showWarningAlert = (message) => {
        Swal.fire({
            title: 'Missing Information',
            text: message,
            icon: 'warning',
            confirmButtonColor: '#ffc107',
            confirmButtonText: 'Got it',
            customClass: {
                popup: 'professional-swal'
            }
        });
    };

    const showPreviewModal = () => {
        if (!title.trim() && !content.trim()) {
            showWarningAlert('Please add content before previewing');
            return;
        }

        Swal.fire({
            title: title.trim() || 'Untitled Manual',
            html: `
                <div class="preview-modal-container">
                    <div class="preview-meta">
                        <div class="preview-meta-item">
                            <span class="meta-label">Status</span>
                            <span class="meta-value status-draft">Draft</span>
                        </div>
                        <div class="preview-meta-item">
                            <span class="meta-label">Last Modified</span>
                            <span class="meta-value">${new Date().toLocaleString()}</span>
                        </div>
                    </div>
                    <div class="preview-content">
                        ${content || '<p class="empty-preview">No content available for preview</p>'}
                    </div>
                </div>
            `,
            width: '900px',
            showConfirmButton: true,
            showCancelButton: true,
            confirmButtonText: 'Edit',
            cancelButtonText: 'Close',
            confirmButtonColor: '#004aad',
            cancelButtonColor: '#6c757d',
            customClass: {
                popup: 'preview-swal',
                confirmButton: 'preview-edit-btn',
                cancelButton: 'preview-close-btn'
            }
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!title.trim()) {
            showWarningAlert('Please enter a title for the manual');
            return;
        }

        if (!content.trim()) {
            showWarningAlert('Please enter content for the manual');
            return;
        }

        setLoading(true);

        try {
            const response = await api.post('/manuals', {
                title: title.trim(),
                content: content,
                videos: videos
            });

            if (response.data.success) {
                const message = response.data.isUpdate
                    ? 'Your manual has been successfully updated'
                    : 'Your manual has been successfully created';

                setIsEditing(true);
                setExistingManual(response.data.data);
                showSuccessAlert(message, response.data.isUpdate);

                // Trigger manual update event
                window.dispatchEvent(new CustomEvent('manualUpdated'));
            }
        } catch (error) {
            console.error('Error saving manual:', error);

            let errorMessage = 'An unexpected error occurred while saving the manual';
            if (error.response?.data?.message) {
                errorMessage = error.response.data.message;
            } else if (error.message) {
                errorMessage = error.message;
            }

            showErrorAlert(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    const handleCancel = () => {
        if (isEditing && (title !== existingManual?.title || content !== existingManual?.content)) {
            Swal.fire({
                title: 'Unsaved Changes',
                text: 'You have unsaved changes. Are you sure you want to leave?',
                icon: 'question',
                showCancelButton: true,
                confirmButtonColor: '#dc3545',
                cancelButtonColor: '#6c757d',
                confirmButtonText: 'Leave',
                cancelButtonText: 'Stay',
                customClass: {
                    confirmButton: 'swal-leave-btn'
                }
            }).then((result) => {
                if (result.isConfirmed) {
                    navigate('/admin/manuals');
                }
            });
        } else {
            navigate('/admin/manuals');
        }
    };

    if (fetching) {
        return (
            <div className="manual-editor">
                <div className="container">
                    <div className="loading-state">
                        <FaSpinner className="loading-spinner-icon" />
                        <p>Loading manual content...</p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="manual-editor">
            <div className="container">
                <div className="editor-header">
                    <div className="header-left">
                        <div className="header-icon">
                            <MdLibraryBooks />
                        </div>
                        <div>
                            <h1>{isEditing ? 'Edit Manual' : 'Create New Manual'}</h1>
                        </div>
                    </div>
                    {isEditing && (
                        <div className="header-right">
                            <div className="last-edited">
                                Last edited: {existingManual?.updatedAt ? new Date(existingManual.updatedAt).toLocaleString() : 'Just now'}
                            </div>
                        </div>
                    )}
                </div>

                <form onSubmit={handleSubmit}>
                    <div className="form-group">
                        <label htmlFor="title">
                            Manual Title
                            <span className="required">*</span>
                        </label>
                        <input
                            type="text"
                            id="title"
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                            placeholder="Enter a descriptive title for your manual"
                            required
                            autoFocus
                            className="title-input"
                        />
                    </div>

                    <div className="form-group">
                        <label>
                            Content
                            <span className="required">*</span>
                        </label>
                        <RichTextEditor
                            initialContent={content}
                            onChange={setContent}
                            placeholder="Start writing your manual content here... Use the toolbar to format text, add images, create tables, and more."
                        />
                    </div>

                    {/* Videos Section */}
                    <div className="form-group">
                        <label>
                            Training Videos
                            <span className="optional">(Optional)</span>
                        </label>

                        {/* Video List */}
                        {videos.length > 0 && (
                            <div className="videos-list">
                                <h4>Uploaded Videos ({videos.length})</h4>
                                {videos.map((video) => (
                                    <div key={video._id} className="video-item">
                                        <div className="video-info">
                                            <FaVideo className="video-icon" />
                                            <div className="video-details">
                                                <strong>{video.title}</strong>
                                                {video.description && <p>{video.description}</p>}
                                                <small>Uploaded: {new Date(video.uploadedAt).toLocaleDateString()}</small>
                                            </div>
                                        </div>
                                        <div className="video-actions">
                                            <a
                                                href={`https://hms.backend.hiragroupcloud.com${video.url}`}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="btn-video-play"
                                            >
                                                <FaPlay /> Play
                                            </a>
                                            <button
                                                type="button"
                                                onClick={() => handleDeleteVideo(video._id)}
                                                className="btn-video-delete"
                                            >
                                                <FaTrash /> Delete
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}

                        {/* Video Upload Section */}
                        {!showVideoUpload ? (
                            <button
                                type="button"
                                onClick={() => setShowVideoUpload(true)}
                                className="btn-add-video"
                            >
                                <FaPlus /> Add Training Video
                            </button>
                        ) : (
                            <div className="video-upload-section">
                                <h4>Upload New Video</h4>
                                <div className="video-upload-form">
                                    <input
                                        type="text"
                                        placeholder="Video Title *"
                                        value={videoTitle}
                                        onChange={(e) => setVideoTitle(e.target.value)}
                                        className="video-title-input"
                                    />
                                    <textarea
                                        placeholder="Video Description (Optional)"
                                        value={videoDescription}
                                        onChange={(e) => setVideoDescription(e.target.value)}
                                        className="video-description-input"
                                        rows="2"
                                    />
                                    <input
                                        type="file"
                                        accept="video/*"
                                        onChange={handleVideoUpload}
                                        className="video-file-input"
                                        id="video-upload"
                                    />
                                    <label htmlFor="video-upload" className="video-file-label">
                                        <FaUpload /> Choose Video File
                                    </label>
                                    {uploadingVideo && (
                                        <div className="uploading-progress">
                                            <FaSpinner className="spinning" />
                                            <span>Uploading video...</span>
                                        </div>
                                    )}
                                    <div className="video-upload-actions">
                                        <button
                                            type="button"
                                            onClick={() => {
                                                setShowVideoUpload(false);
                                                setVideoTitle('');
                                                setVideoDescription('');
                                            }}
                                            className="btn-cancel-upload"
                                        >
                                            Cancel
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>

                    <div className="button-group">
                        <button
                            type="button"
                            onClick={showPreviewModal}
                            className="btn-preview"
                            disabled={loading}
                        >
                            <MdPreview />
                            <span>Preview</span>
                        </button>
                        <button
                            type="button"
                            onClick={handleCancel}
                            className="btn-secondary"
                            disabled={loading}
                        >
                            <FaTimes />
                            <span>Cancel</span>
                        </button>
                        <button
                            type="submit"
                            className="btn-primary"
                            disabled={loading}
                        >
                            {loading ? (
                                <>
                                    <FaSpinner className="spinning" />
                                    <span>Saving...</span>
                                </>
                            ) : (
                                <>
                                    {isEditing ? <FaSave /> : <FaPlus />}
                                    <span>{isEditing ? 'Update Manual' : 'Create Manual'}</span>
                                </>
                            )}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default ManualEditor;